// components/shared/Pagination/Pagination.tsx
import { Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

export interface IPagination {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: IPagination) {
  const renderPageNumbers = () => {
    const pages: (number | string)[] = [];

    // Always display page 1
    pages.push(1);

    // The logic displays pages around the current page
    if (totalPages <= 2) {
      // If total number of pages <= 2, show all
      for (let i = 2; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate the page space you want to display around the current page
      const delta = 2;
      const left = Math.max(2, currentPage - delta);
      const right = Math.min(totalPages - 1, currentPage + delta);

      // Add a mark ... if the distance from page 1 to left > 1
      if (left > 2) {
        pages.push("…");
      }

      // Add pages from left to right
      for (let i = left; i <= right; i++) {
        pages.push(i);
      }

      // Add a mark ... if the distance from right to totalPages > 1
      if (right < totalPages - 1) {
        pages.push("…");
      }

      // Always display the last page if it's different from the last page displayed
      if (totalPages > 1 && pages[pages.length - 1] !== totalPages) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pages = renderPageNumbers();

  return (
    <nav aria-label="Pagination">
      <ul className="pagination-modern">
        {/* Button Prev */}
        <li
          className={`page-item-modern ${currentPage === 1 ? "disabled" : ""}`}
        >
          <Link
            to="#"
            className="page-link-modern"
            onClick={() => onPageChange(currentPage - 1)}
            aria-label="Previous"
          >
            <FiChevronLeft size={16} />
          </Link>
        </li>

        {/* Page numbers */}
        {pages.map((page, idx) => (
          <li
            key={idx}
            className={`page-item-modern ${page === currentPage ? "active" : ""} ${
              page === "…" ? "dots" : ""
            }`}
          >
            {page === "…" ? (
              <span className="page-link-modern">…</span>
            ) : (
              <Link
                to="#"
                className="page-link-modern"
                onClick={() => onPageChange(page as number)}
              >
                {page}
              </Link>
            )}
          </li>
        ))}

        {/* Button Next */}
        <li
          className={`page-item-modern ${currentPage === totalPages ? "disabled" : ""}`}
        >
          <Link
            to="#"
            className="page-link-modern"
            onClick={() => onPageChange(currentPage + 1)}
            aria-label="Next"
          >
            <FiChevronRight size={16} />
          </Link>
        </li>
      </ul>

      <style>{`
        .pagination-modern {
          display: flex;
          gap: 4px;
          list-style: none;
          padding: 0;
          margin: 0;
          align-items: center;
          flex-wrap: wrap;
        }
        .page-item-modern {
          display: inline-block;
        }
        .page-link-modern {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 34px;
          height: 34px;
          padding: 0 8px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #1e293b;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: border-color 0.15s ease;
          cursor: pointer;
        }
        .page-link-modern:hover {
          border-color: #94a3b8;
          background: #f8fafc;
        }
        .page-item-modern.active .page-link-modern {
          border-color: #4f46e5;
          background: #4f46e5;
          color: #ffffff;
        }
        .page-item-modern.active .page-link-modern:hover {
          background: #4338ca;
          border-color: #4338ca;
        }
        .page-item-modern.disabled .page-link-modern {
          opacity: 0.4;
          pointer-events: none;
          cursor: default;
        }
        .page-item-modern.dots .page-link-modern {
          border: none;
          background: transparent;
          cursor: default;
          pointer-events: none;
          color: #94a3b8;
        }
        /* Responsive */
        @media (max-width: 576px) {
          .page-link-modern {
            min-width: 30px;
            height: 30px;
            font-size: 12px;
            padding: 0 6px;
          }
        }
      `}</style>
    </nav>
  );
}
